/**
 * Stream Cache - IndexedDB Persistence for Stream Data
 *
 * Provides persistent caching of stream data in IndexedDB for:
 * - Instant stream switching (load from cache while fetching delta)
 * - Offline support (view last cached state when disconnected)
 * - Reduced bandwidth (only fetch changes since lastSeq)
 *
 * Uses idb-keyval for simple key-value storage in IndexedDB.
 */

import { get, set, del, keys } from 'idb-keyval';
import type { StreamWithGraph, Node, Edge, StreamEvent } from '../types/api';
import { axiom } from './axiom';

// Prefix for stream cache keys
const STREAM_PREFIX = 'stream:';
const CACHE_VERSION = 1;

// Maximum age before cache is considered stale (24 hours)
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000;

// Maximum number of streams to keep in cache (LRU)
const MAX_CACHED_STREAMS = 10;

/**
 * Cached stream data structure
 */
export interface CachedStream {
  version: number;
  data: StreamWithGraph;
  lastSeq: number;
  cachedAt: string;
  accessedAt: string;
}

/**
 * Load stream data from cache
 */
export async function loadStreamFromCache(streamId: string): Promise<CachedStream | null> {
  try {
    const cached = await get<CachedStream>(`${STREAM_PREFIX}${streamId}`);

    if (!cached) {
      return null;
    }

    // Check cache version compatibility
    if (cached.version !== CACHE_VERSION) {
      axiom.info(`[StreamCache] Cache version mismatch for ${streamId.slice(0, 8)}, invalidating`);
      await del(`${STREAM_PREFIX}${streamId}`);
      return null;
    }

    // Check if cache is too old
    const cacheAge = Date.now() - new Date(cached.cachedAt).getTime();
    if (cacheAge > MAX_CACHE_AGE_MS) {
      axiom.info(`[StreamCache] Cache expired for ${streamId.slice(0, 8)}`);
      await del(`${STREAM_PREFIX}${streamId}`);
      return null;
    }

    // Update access time for LRU
    cached.accessedAt = new Date().toISOString();
    await set(`${STREAM_PREFIX}${streamId}`, cached);

    axiom.info(`[StreamCache] Loaded ${streamId.slice(0, 8)} from cache (seq=${cached.lastSeq})`);
    return cached;
  } catch (error) {
    axiom.error('[StreamCache] Error loading from cache', { error: error instanceof Error ? error.message : String(error) });
    return null;
  }
}

/**
 * Save stream data to cache
 */
export async function saveStreamToCache(
  streamId: string,
  data: StreamWithGraph,
  lastSeq: number
): Promise<void> {
  try {
    const now = new Date().toISOString();
    const cached: CachedStream = {
      version: CACHE_VERSION,
      data,
      lastSeq,
      cachedAt: now,
      accessedAt: now,
    };

    await set(`${STREAM_PREFIX}${streamId}`, cached);
    axiom.info(`[StreamCache] Saved ${streamId.slice(0, 8)} to cache (seq=${lastSeq})`);

    // Enforce LRU limit
    await enforceCacheLimit();
  } catch (error) {
    axiom.error('[StreamCache] Error saving to cache', { error: error instanceof Error ? error.message : String(error) });
  }
}

/**
 * Apply a stream event to the cached data
 */
export async function applyEventToCache(
  streamId: string,
  event: StreamEvent
): Promise<void> {
  try {
    const cached = await get<CachedStream>(`${STREAM_PREFIX}${streamId}`);
    if (!cached) {
      return;
    }

    // Apply the event based on type
    switch (event.type) {
      case 'node_created':
        if (event.node) {
          cached.data.nodes.push(event.node as Node);
        }
        break;

      case 'node_updated':
        if (event.node_id && event.changes) {
          const nodeIndex = cached.data.nodes.findIndex(n => n.id === event.node_id);
          if (nodeIndex >= 0) {
            // Convert event.changes.meta (object) to string for storage if present
            const changes = { ...event.changes } as Partial<Node>;
            if ('meta' in changes && changes.meta !== undefined && changes.meta !== null && typeof changes.meta === 'object') {
              changes.meta = JSON.stringify(changes.meta);
            }
            cached.data.nodes[nodeIndex] = {
              ...cached.data.nodes[nodeIndex],
              ...changes,
            };
          }
        }
        break;

      case 'node_deleted':
        if (event.node_id) {
          cached.data.nodes = cached.data.nodes.filter(n => n.id !== event.node_id);
          // Also remove edges connected to this node
          cached.data.edges = cached.data.edges.filter(
            e => e.from_node_id !== event.node_id && e.to_node_id !== event.node_id
          );
        }
        break;

      case 'edge_created':
        if (event.edge) {
          cached.data.edges.push(event.edge as Edge);
        }
        break;

      case 'edge_deleted':
        if (event.edge_id) {
          cached.data.edges = cached.data.edges.filter(e => e.id !== event.edge_id);
        }
        break;
    }

    // Update lastSeq
    if (event.seq !== undefined && event.seq > cached.lastSeq) {
      cached.lastSeq = event.seq;
    }

    // Save updated cache
    await set(`${STREAM_PREFIX}${streamId}`, cached);
  } catch (error) {
    axiom.error('[StreamCache] Error applying event to cache', { error: error instanceof Error ? error.message : String(error) });
  }
}

/**
 * Update the lastSeq for a stream without modifying data
 */
export async function updateCacheSeq(streamId: string, lastSeq: number): Promise<void> {
  try {
    const cached = await get<CachedStream>(`${STREAM_PREFIX}${streamId}`);
    if (cached && lastSeq > cached.lastSeq) {
      cached.lastSeq = lastSeq;
      await set(`${STREAM_PREFIX}${streamId}`, cached);
    }
  } catch (error) {
    axiom.error('[StreamCache] Error updating cache seq', { error: error instanceof Error ? error.message : String(error) });
  }
}

/**
 * Get the lastSeq for a stream from cache (for delta sync)
 */
export async function getCacheLastSeq(streamId: string): Promise<number> {
  try {
    const cached = await get<CachedStream>(`${STREAM_PREFIX}${streamId}`);
    return cached?.lastSeq || 0;
  } catch {
    return 0;
  }
}

/**
 * Invalidate cache for a stream
 */
export async function invalidateStreamCache(streamId: string): Promise<void> {
  try {
    await del(`${STREAM_PREFIX}${streamId}`);
    axiom.info(`[StreamCache] Invalidated cache for ${streamId.slice(0, 8)}`);
  } catch (error) {
    axiom.error('[StreamCache] Error invalidating cache', { error: error instanceof Error ? error.message : String(error) });
  }
}

/**
 * Clear all stream caches
 */
export async function clearAllStreamCaches(): Promise<void> {
  try {
    const allKeys = await keys();
    const streamKeys = allKeys.filter(
      key => typeof key === 'string' && key.startsWith(STREAM_PREFIX)
    );

    for (const key of streamKeys) {
      await del(key);
    }

    axiom.info(`[StreamCache] Cleared ${streamKeys.length} cached streams`);
  } catch (error) {
    axiom.error('[StreamCache] Error clearing caches', { error: error instanceof Error ? error.message : String(error) });
  }
}

/**
 * Enforce LRU cache limit by removing least recently accessed streams
 */
async function enforceCacheLimit(): Promise<void> {
  try {
    const allKeys = await keys();
    const streamKeys = allKeys.filter(
      key => typeof key === 'string' && key.startsWith(STREAM_PREFIX)
    );

    if (streamKeys.length <= MAX_CACHED_STREAMS) {
      return;
    }

    // Get all cached streams with their access times
    const streams: Array<{ key: string; accessedAt: string }> = [];
    for (const key of streamKeys) {
      const cached = await get<CachedStream>(key as string);
      if (cached) {
        streams.push({ key: key as string, accessedAt: cached.accessedAt });
      }
    }

    // Sort by access time (oldest first)
    streams.sort((a, b) => new Date(a.accessedAt).getTime() - new Date(b.accessedAt).getTime());

    // Remove oldest entries until we're under the limit
    const toRemove = streams.slice(0, streams.length - MAX_CACHED_STREAMS);
    for (const { key } of toRemove) {
      await del(key);
      axiom.info(`[StreamCache] LRU evicted: ${key}`);
    }
  } catch (error) {
    axiom.error('[StreamCache] Error enforcing cache limit', { error: error instanceof Error ? error.message : String(error) });
  }
}
